'use client';

import { c as _c } from "react/compiler-runtime";
import { Divider } from '@7f-ui/components/divider/divider';
import { MessageBox } from '@7f-ui/components/message-box/message-box';
import { useCart } from '@feat/feat-cart/cart';
import { CartCurrencyValueProvider } from '@feat/feat-cart/currency';
import { getCartTrackObjectItems } from '@feat/feat-cart/tracking';
import { GERMAN_LOCALE } from '@utils/utils-format';
import { shopTestId } from '@utils/utils-shop-test-ids';
import dynamic from 'next/dynamic';
import { useEffect } from 'react';
import { AccommodationMicrodata } from 'src/lib/hotel-ads/microdata';
import { getHotelAdsCartItem } from '../_lib/hotel-ads-cart-item';
import { AppCartList } from './app-cart-list';
import { CartCTABox } from './cart-cta-box';
import { CartEmpty } from './cart-empty';
import { SkeletonCartItem } from './cart-item/skeleton-cart-item';
const CartRecommendations = dynamic(() => import('./cart-recommendations').then(mod => ({
  default: mod.CartRecommendations
})), {
  loading: () => null
});
const CartWhyBook = dynamic(() => import('./cart-why-book').then(mod => ({
  default: mod.CartWhyBook
})), {
  loading: () => null
});
const CartTrustedReviews = dynamic(() => import('./cart-trusted-reviews').then(mod => ({
  default: mod.CartTrustedReviews
})), {
  loading: () => null
});
const CartTrustSlider = dynamic(() => import('./cart-trust-slider').then(mod => ({
  default: mod.CartTrustSlider
})), {
  loading: () => null
});
type CartContentProps = {
  checkoutHref: string;
};
export function CartContent({
  checkoutHref
}: CartContentProps) {
  const {
    cart,
    isLoading
  } = useCart();
  const language = GERMAN_LOCALE;
  useEffect(() => {
    if (!cart?.items.all.length) return;
    void import('@feat/feat-tracking').then(({
      trackViewCart
    }) => {
      trackViewCart(cart.total, cart.currency, getCartTrackObjectItems(cart.items.all));
    });
  }, [cart]);
  if (isLoading && !cart) return <CartInitialSkeleton />;
  if (!cart) return null;
  if (cart.isEmpty) return <CartEmpty />;
  const currencies = [...new Set(cart.items.all.map(item => item.currency))];
  const hasMixedCurrencies = currencies.length > 1;
  const hotelAdsItem = getHotelAdsCartItem(cart);
  const columns = <>
      <div className="col-span-12 sm:col-span-7 lg:col-span-6">
        {hasMixedCurrencies && <MessageBox type="error" className="mb-8 sm:mb-10" testId={shopTestId('Cart.mixed-currencies')}>
            {`Dein Warenkorb enthält Produkte in ${new Intl.ListFormat(language, {
          type: 'conjunction'
        }).format(currencies)}. Entferne die Produkte einer Währung, um zur Kasse zu gehen.`}
          </MessageBox>}
        <CartCurrencyValueProvider cartCurrency={cart.currency}>
          <AppCartList cart={cart} />
        </CartCurrencyValueProvider>
      </div>

      <CartCTABox cart={cart} checkoutHref={checkoutHref} />
    </>;
  const content = <div className="fluid-grid">
      {hotelAdsItem ? <AccommodationMicrodata accommodationId={hotelAdsItem.accommodation.id}>
          {columns}
        </AccommodationMicrodata> : columns}
    </div>;
  return <>
      <div className="fluid-grid">
        <h1 className="col-span-12 pb-8 font-heading1 sm:pb-12 lg:pb-16">Warenkorb</h1>
      </div>
      {content}

      <Divider className="my-8 sm:my-12" />
      <CartTrustedReviews />
      <Divider className="my-8 sm:my-12" />
      <CartWhyBook />
      <CartRecommendations />
      <Divider className="my-8 sm:my-12" />
      <CartTrustSlider />
    </>;
}
function CartInitialSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="fluid-grid"><div className="col-span-12 pb-8 sm:pb-12 lg:pb-16"><div className="h-10 w-48 animate-pulse rounded bg-primary/10" /></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <>{t0}<div className="fluid-grid"><SkeletonCartItem /></div></>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}